import { useTranslation } from "react-i18next";
import { Wrench, ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { PtActionData } from "@/hooks/useTasksQuery";

interface PtActionCardProps {
  /** The assigned PT-TSK action (defect_action.assigned_to = current user), L6-3 / TES-1869. */
  action: PtActionData;
  /** Navigate to the parent defect (the action belongs to a defect). */
  onOpenDefect?: (defectId: string) => void;
}

/**
 * "Meine Aufgaben" card for an assigned PT-TSK-Maßnahme (defect_action.assigned_to).
 * Presentational only; navigation is delegated via onOpenDefect (parent wires it to the defect
 * detail route). Shows the linked PT-TSK task-group status (defect_task_links.pt_task_status)
 * when present — the unified view surfaces the linked task-group status, not per-user PT-TSK tasks.
 */
export function PtActionCard({ action, onOpenDefect }: PtActionCardProps) {
  const { t } = useTranslation();

  return (
    <Card data-testid="pt-action-card" className="transition-colors hover:bg-muted/40">
      <CardContent className="flex items-center gap-3 p-4">
        <Wrench className="h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="shrink-0">
              {t("tasks.ptAction")}
            </Badge>
            {action.pt_task_status && (
              <Badge variant="outline" className="shrink-0">
                {action.pt_task_status}
              </Badge>
            )}
          </div>
          <p className="mt-1 truncate font-medium" title={action.title}>
            {action.title}
          </p>
          {action.description && (
            <p className="truncate text-sm text-muted-foreground" title={action.description}>
              {action.description}
            </p>
          )}
        </div>
        {onOpenDefect && (
          <Button
            variant="ghost"
            size="sm"
            className="shrink-0"
            onClick={() => onOpenDefect(action.defect_id)}
          >
            {t("tasks.openDefect")}
            <ChevronRight className="ml-1 h-4 w-4" aria-hidden="true" />
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
